import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Restaurant,
  LunchDining,
  LocalPizza,
  Icecream,
  LocalCafe,
  GridViewRounded,
} from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";
import { useDragScroll } from "../../hooks/useDragScroll";

const categoryIcons = {
  Pizza: LocalPizza,
  "Main Course": LunchDining,
  Salads: Restaurant,
  Desserts: Icecream,
  Beverages: LocalCafe,
  all: GridViewRounded,
};

const CategorySidebar = ({
  categories,
  selectedCategory,
  onCategoryChange,
  loading = false,
}) => {
  const theme = useTheme();
  const { scrollContainerRef, isDragging, handlers } = useDragScroll();

  return (
    <Box
      sx={{
        width: 200,
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        bgcolor: "#fafafa",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 1.5,
        flexShrink: 0, // Prevent sidebar from shrinking
        minHeight: 0, // Allow proper flex behavior
      }}
    >
      {/* Scrollable Categories List */}
      <Box
        ref={scrollContainerRef}
        {...handlers}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.primary.main, 0.3),
            borderRadius: "10px",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.5),
            },
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          categories.map((category) => {
            const CategoryIcon = categoryIcons[category.name] || Restaurant;
            const isSelected = selectedCategory === category.id;
            return (
              <Card
                key={category.id}
                elevation={0}
                onClick={() => onCategoryChange(category.id)}
                sx={{
                  cursor: "pointer",
                  mb: 1.5,
                  border: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  bgcolor: "background.paper",
                  borderRadius: 2.5,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isSelected
                    ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`
                    : `0 3px 10px ${alpha(theme.palette.primary.main, 0.08)}, 0 1px 4px ${alpha(theme.palette.grey[500], 0.06)}`,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    transform: "translateY(-3px)",
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.05),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.2s ease",
                        overflow: "hidden",
                      }}
                    >
                      {category.image ? (
                        <ImageLoader
                          imagePath={category.image}
                          width={44}
                          height={44}
                          alt={category.name}
                          borderRadius={2}
                        />
                      ) : (
                        <CategoryIcon
                          sx={{
                            fontSize: 22,
                            color: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            transition: "all 0.2s ease",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 700 : 600,
                        textTransform: "capitalize",
                        fontSize: "0.85rem",
                        color: isSelected
                          ? theme.palette.primary.main
                          : "text.primary",
                        lineHeight: 1.3,
                        flex: 1,
                      }}
                    >
                      {category.name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CategorySidebar;
